import { supabase } from '../config/supabase'
import { AnalyticsRow, FetchBetsParams, mapAnalyticsRowToBet, BetUIModel, buildSelectionId } from '../types/analytics'

const SCHEMA_NAME = 'gold'
const TABLE_NAME = 'bet_analytics_current'

const DEFAULT_LIMIT = 30

export const fetchBets = async (params: FetchBetsParams = {}): Promise<{ data: BetUIModel[]; error: string | null }> => {
  const {
    sportKey,
    // allow filtering by human-readable sport titles
    sportTitles,
    marketKey,
    bookmakerKey,
    bookmakerTitles,
    eventName,
    homeTeam,
    awayTeam,
    evFilter = 'all',
    isLive,
    region,
    fromTime,
    toTime,
    limit = DEFAULT_LIMIT,
    offset = 0,
    sort = 'commence_time_asc',
  } = params

  // Build base query selecting only required columns
  let query = supabase
    .schema(SCHEMA_NAME)
    .from(TABLE_NAME as any)
    .select(
      [
        'event_id',
        'sport_key',
        'sport_title',
        'region',
        'bookmaker_key',
        'bookmaker_title',
        'market_key',
        'outcome_name',
        'outcome_point',
        'market_last_update',
        'event_commence_time',
        'home_team',
        'away_team',
        'price_decimal',
        'implied_prob_raw',
        'market_overround',
        'market_vig_pct',
        'implied_prob_equal',
        'fair_odds_equal',
        'implied_prob_weighted',
        'fair_odds_weighted',
        'pinnacle_prob_devig',
        'pinnacle_last_update',
        'expected_value',
        'expected_value_pct',
      ].join(',')
    )
    .range(offset, offset + limit - 1)

  // Filters
  if (sportKey) query = query.eq('sport_key', sportKey)
  if (sportTitles && sportTitles.length > 0) query = query.in('sport_title', sportTitles)
  if (marketKey) query = query.eq('market_key', marketKey)
  if (bookmakerKey) query = query.eq('bookmaker_key', bookmakerKey)
  if (bookmakerTitles && bookmakerTitles.length > 0) query = query.in('bookmaker_title', bookmakerTitles)
  if (region) query = query.eq('region', region)
  if (fromTime) query = query.gte('event_commence_time', fromTime)
  if (toTime) query = query.lte('event_commence_time', toTime)

  if (typeof isLive === 'boolean') {
    const nowIso = new Date().toISOString()
    if (isLive) {
      query = query.lte('event_commence_time', nowIso)
    } else {
      query = query.gte('event_commence_time', nowIso)
    }
  }

  if (evFilter === 'positive') query = query.gt('expected_value', 0)
  if (evFilter === 'negative') query = query.lt('expected_value', 0)

  if (eventName && eventName.trim().length > 0) {
    const term = `%${eventName.trim()}%`
    // ILIKE on both teams; Supabase supports or() filter
    query = query.or(`home_team.ilike.${term},away_team.ilike.${term}`)
  }

  // Dedicated team filters
  if (homeTeam && homeTeam.trim().length > 0) {
    const term = `%${homeTeam.trim()}%`
    query = query.ilike('home_team', term as any)
  }
  if (awayTeam && awayTeam.trim().length > 0) {
    const term = `%${awayTeam.trim()}%`
    query = query.ilike('away_team', term as any)
  }

  // Sorting
  if (sort === 'commence_time_asc') query = query.order('event_commence_time', { ascending: true })
  if (sort === 'commence_time_desc') query = query.order('event_commence_time', { ascending: false })
  if (sort === 'updated_desc') query = query.order('market_last_update', { ascending: false })
  if (sort === 'updated_asc') query = query.order('market_last_update', { ascending: true })
  if (sort === 'odds_asc') query = query.order('price_decimal', { ascending: true, nullsFirst: false })
  if (sort === 'odds_desc') query = query.order('price_decimal', { ascending: false, nullsFirst: false })
  if (sort === 'vig_asc') query = query.order('market_vig_pct', { ascending: true, nullsFirst: false })
  if (sort === 'vig_desc') query = query.order('market_vig_pct', { ascending: false, nullsFirst: false })
  if (sort === 'fair_odds_asc') query = query.order('fair_odds_weighted', { ascending: true, nullsFirst: false })
  if (sort === 'fair_odds_desc') query = query.order('fair_odds_weighted', { ascending: false, nullsFirst: false })
  if (sort === 'implied_prob_asc') query = query.order('implied_prob_weighted', { ascending: true, nullsFirst: false })
  if (sort === 'implied_prob_desc') query = query.order('implied_prob_weighted', { ascending: false, nullsFirst: false })
  if (sort === 'true_win_prob_asc') query = query.order('pinnacle_prob_devig', { ascending: true, nullsFirst: false })
  if (sort === 'true_win_prob_desc') query = query.order('pinnacle_prob_devig', { ascending: false, nullsFirst: false })
  if (sort === 'expected_value_asc') query = query.order('expected_value', { ascending: true, nullsFirst: false })
  if (sort === 'expected_value_desc') query = query.order('expected_value', { ascending: false, nullsFirst: false })

  // Tiebreaker: commence time ascending for all sorts other than commence time
  if (sort !== 'commence_time_asc' && sort !== 'commence_time_desc') {
    query = query.order('event_commence_time', { ascending: true })
  }

  const { data, error } = await query

  if (error) {
    return { data: [], error: error.message }
  }

  const rows: AnalyticsRow[] = (data ?? []) as unknown as AnalyticsRow[]

  // Deduplicate rows representing the same selection (event/bookmaker/market/outcome/point/region)
  // Keep the row with the most recent market_last_update
  const byId = new Map<string, AnalyticsRow>()
  for (const row of rows) {
    const id = buildSelectionId(row)
    const existing = byId.get(id)
    if (!existing) {
      byId.set(id, row)
      continue
    }
    const existingTs = new Date(existing.market_last_update).getTime()
    const currentTs = new Date(row.market_last_update).getTime()
    if (Number.isFinite(currentTs) && (!Number.isFinite(existingTs) || currentTs > existingTs)) {
      byId.set(id, row)
    }
  }

  const bets = Array.from(byId.values()).map(mapAnalyticsRowToBet)
  return { data: bets, error: null }
}


export const fetchDistinctBookmakerTitles = async (): Promise<{ data: string[]; error: string | null }> => {
  // Page through results to avoid server-side row caps (e.g., 1000 default)
  const pageSize = 1000
  let offset = 0
  const collected = new Set<string>()

  while (true) {
    const { data, error } = await supabase
      .schema(SCHEMA_NAME)
      .from(TABLE_NAME as any)
      .select('bookmaker_title')
      .not('bookmaker_title', 'is', null as any)
      .order('bookmaker_title', { ascending: true })
      .range(offset, offset + pageSize - 1)

    if (error) {
      return { data: [], error: error.message }
    }

    const rows = (data ?? []) as { bookmaker_title: string | null }[]
    for (const row of rows) {
      if (row.bookmaker_title) collected.add(row.bookmaker_title)
    }

    if (rows.length < pageSize) break
    offset += pageSize
  }

  return { data: Array.from(collected).sort((a, b) => a.localeCompare(b)), error: null }
}

export const fetchDistinctSportTitles = async (): Promise<{ data: string[]; error: string | null }> => {
  const pageSize = 1000
  let offset = 0
  const collected = new Set<string>()

  while (true) {
    const { data, error } = await supabase
      .schema(SCHEMA_NAME)
      .from(TABLE_NAME as any)
      .select('sport_title')
      .not('sport_title', 'is', null as any)
      .order('sport_title', { ascending: true })
      .range(offset, offset + pageSize - 1)

    if (error) {
      return { data: [], error: error.message }
    }

    const rows = (data ?? []) as { sport_title: string | null }[]
    for (const row of rows) {
      if (row.sport_title) collected.add(row.sport_title)
    }

    if (rows.length < pageSize) break
    offset += pageSize
  }

  return { data: Array.from(collected).sort((a, b) => a.localeCompare(b)), error: null }
}


// Lightweight rows for bookmaker coverage aggregation across the entire table
export interface CoverageRow {
  event_id: string;
  region: string | null;
  bookmaker_title: string;
  market_key: string;
  market_vig_pct: number | null;
  expected_value: number | null;
}

/**
 * Fetches all rows needed to compute bookmaker coverage metrics by paging through
 * gold.bet_analytics_current with a minimal projection. Returns every row, not just
 * a subset used elsewhere for UI cards.
 */
export const fetchAllCoverageRows = async (): Promise<{ data: CoverageRow[]; error: string | null }> => {
  const pageSize = 1000
  let offset = 0
  const rows: CoverageRow[] = []

  while (true) {
    const { data, error } = await supabase
      .schema(SCHEMA_NAME)
      .from(TABLE_NAME as any)
      .select('event_id,region,bookmaker_title,market_key,market_vig_pct,expected_value')
      .range(offset, offset + pageSize - 1)

    if (error) {
      return { data: [], error: error.message }
    }

    const page = (data ?? []) as CoverageRow[]
    rows.push(...page)
    if (page.length < pageSize) break
    offset += pageSize
  }

  return { data: rows, error: null }
}


